import { useEffect, useRef, useState } from "react";
import { PackingGroup } from "@boxem/core-api-schema";

import styles from "./AssignAllSkusModal.module.css";

import { FbaBatch, FbaBox, getItemAssignedQuantity, useShipmentContext } from "@/contexts/ShipmentContext";
import Button from "@/components/forms/Button";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import IconBoxPlaceItem from "@/components/icons/IconBoxPlaceItem";
import IconInfo from "@/components/icons/IconInfo";
import { Modal, ModalFooter, ModalHeader } from "@/components/Modal/Modal";
import IconCaution from "@/components/icons/IconCaution";
import IconPlusCircle from "@/components/icons/IconPlusCircle";
import IconCheckCircle from "@/components/icons/IconCheckCircle";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import Tooltip from "@/components/Tooltip";

import { usePrinterContext } from "@/contexts/PrinterContext";
import { Divider, MultiSelect } from "@/components/forms/Select";
import SelectItems, { SelectableItem as SelectableItemWithoutState } from "./SelectItemsModalBody";
import IconBox from "@/components/icons/IconBox";
import { BoxUpdate } from "./PrepAndBoxemPage";
import { addToBoxes, Item } from "./PackingGroup";

export type AssignAllSkusModalProps = {
  packingGroupId?: string;
  items?: Item[];
  shipmentId?: number;
  selectedBoxes: FbaBox[];
  index?: number;
  open?: boolean;
  onClose: () => void;
  onError: (errors: any) => void;
  createNewBox: () => Promise<FbaBox>;
  onUpdates: (updates: BoxUpdate[]) => void;
};

type SelectableItem = SelectableItemWithoutState & {
  quantityToAssign: number;
}

type BoxState = {
  box: FbaBox;
  selected: boolean;
};

export default function AssignAllSkusModal({
  packingGroupId,
  items: propsItems,
  shipmentId,
  selectedBoxes,
  index,
  open,
  onClose: _onClose,
  createNewBox,
  onError,
  onUpdates,
}: AssignAllSkusModalProps) {
  const loadItems = () => (propsItems ?? [])
    .map(item => ({ ...item, selected: false, quantityToAssign: item.quantity - item.assigned }))
    .sort((l, r) => l.done === r.done ? 0 : l.done ? 1 : -1);

  const totalUnits = propsItems?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<SelectableItem[]>(loadItems);
  const [boxes, setBoxes] = useState<BoxState[]>([]);
  const [selectNewBox, setSelectNewBox] = useState<number>(null);
  const { printFNSKULabel } = usePrinterContext();
  const { currentBatch: batch } = useShipmentContext();

  useEffect(() => setItems(loadItems()), [propsItems, open]);

  // we only use the selected boxes in the packing group when we initially load the dialog
  useEffect(() => {
    if (batch && packingGroupId) {
      setBoxes(batch.boxes
        .filter(box => box.packingGroupId === packingGroupId && box.shipmentId === shipmentId)
        .map(box => ({ box, selected: selectedBoxes.find(b => b === box) != null }))
      );
    }
  }, [packingGroupId, open]);

  useEffect(() => {
    if (selectNewBox != null) {
      const box = batch.boxes.find(box => box.id === selectNewBox);
      if (box) {
        setSelectNewBox(null);
        setBoxes([...boxes, { box, selected: true }]);
      }
    }
  }, [selectNewBox, batch, boxes]);

  const onClose = () => {
    _onClose();
    setTimeout(() => {
      if (packingGroupId) {
        setItems([]);
      }
    }, 400); // gives time for the dialog transition to finish before changing state
  };

  const setQuantityToAssign = (item: SelectableItem, quantityToAssign: number) => {
    item.quantityToAssign = quantityToAssign;
    setItems([...items]);
  };
  const selectAll = (selected: boolean) => setItems(items.map(item => ({...item, selected: selected && !item.done})));
  const assignClicked = useRef<() => Promise<void>>();
  assignClicked.current = async () => {
    setLoading(true);
    const packingGroup = batch.chosenPackingOption.packingGroups.find(pg => pg.inboundPackingGroupId === packingGroupId);
    const udpates = await addToBoxes(
      packingGroup,
      items.filter(item => item.selected && item.quantityToAssign > 0)
        .map(({batchItem: item, quantityToAssign}) => ({item, quantity: quantityToAssign})),
      boxes.filter(box => box.selected).map(box => box.box),
      onError,
      batch.printFNSKUAfterIncreaseQuantityInBox ? printFNSKULabel : null,
    );
    setLoading(false);
    onUpdates(udpates);
    onClose();
  };

  const zeroItemsToAssign = null == items.find(item => item.selected && item.quantityToAssign > 0);
  const zeroBoxesSelected = null == boxes.find(bs => bs.selected);

  if (items.length < 1) return null;

  return (
    <Modal
      open={open}
      closeOnOutsideClick={false}
      onClose={onClose}
    >
      <ModalHeader className={styles.header}>
        <IconBoxPlaceItem size={32} style={{ color: 'var(--color-primary)' }}/>
        {index !== undefined ? (
          <><span className="strong">Pack Group {index + 1}</span> | {totalUnits} Total Units</>
        ) : (
          <>{totalUnits} Total Units</>
        )}
      </ModalHeader>
      <SelectItems<SelectableItem>
        topRowContent={(
          <>
            <IconInfo />
            <span style={{whiteSpace: "nowrap"}}>The selected items will be assigned to:</span>
            <MultiSelect
              style={{
                minWidth: "154px",
                overflow: "hidden",
                flex: "1",
              }}
              fullWidth={true}
              placeholder={<span className={`${styles.placeholder} ${"weak"}`}>No boxes selected</span>}
              value={boxes.filter(b => b.selected)}
              onOptionClick={async (option, i, closeDropdown) => {
                if ("createBox" in option) {
                  closeDropdown();
                  const newBox = await createNewBox();
                  setSelectNewBox(newBox.id);
                } else {
                  option.selected = !option.selected; setBoxes([...boxes]);
                }
              }}
              options={[...boxes, Divider, { createBox: true }] as BoxState[]}
              optionRender={option => {
                if ("createBox" in option) {
                  return <span className={`${styles.boxOption} ${styles.createBox}`}><IconPlusCircle /> Create Box</span>
                }
                return (
                  <span className={styles.boxOption}>
                    <IconBox />
                    <span style={{flex: 1}}>Box {option.box.displayIndex}</span>
                    <Checkbox value={option.selected} />
                  </span>
                );
              }}
              selectionRender={options => (
                <span
                  style={{
                    width: "0",
                    overflow: "hidden",
                    flex: "1",
                    textOverflow: "ellipsis",
                  }}
                >
                  {options.map(({option}) => `Box ${option.box.displayIndex}`).join(", ")}
                </span>
              )}
            />
          </>
        )}
        productContent={({quantity, assigned, quantityToAssign}) => {
          const allAssigned = assigned + quantityToAssign >= quantity;
          return allAssigned
            ? <span className="inline-positive"><IconCheckCircle />&nbsp;{quantity} / {quantity}</span>
            : <span className="inline-notice"><IconCaution />&nbsp;{assigned + quantityToAssign} / {quantity}</span>;
        }}
        afterProductContent={(item) => (!item.done &&
          <>
            <InputNumber
              className={styles.smallInput}
              value={item.quantityToAssign}
              max={item.quantity - item.assigned}
              onChange={value => setQuantityToAssign(item, value)}
            />
          </>
        )}
        items={items}
        setItems={setItems}
        selectAll={selectAll}
      />
      <ModalFooter className={styles.footer}>
        <Button type="secondary" onClick={onClose}>Cancel</Button>
        <Tooltip
          title={zeroItemsToAssign
            ? "Select SKUs to assign" : zeroBoxesSelected
            ? "Select one or more boxes"
            : null}
          delayMs={0}
        >
          <Button
            onClick={() => assignClicked.current()}
            disabled={zeroItemsToAssign || zeroBoxesSelected}
            disableWhileOnClick={true}
          >
            Assign
          </Button>
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
}
